"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  segments as seedSegments,
  vendors as seedVendors,
  validateSegment,
  validateVendorName,
  deleteSegmentBlockReason,
  deleteVendorBlockReason,
  type FieldError,
  type Segment,
  type SegmentInput,
  type Vendor,
} from "@/data/catalog";

type VendorResult =
  | { ok: true; vendor: Vendor }
  | { ok: false; message: string };

type SegmentResult =
  | { ok: true; segment: Segment }
  | ({ ok: false } & FieldError);

interface CatalogContextValue {
  vendors: Vendor[];
  segments: Segment[];
  createVendor: (name: string) => VendorResult;
  updateVendorName: (id: number, name: string) => VendorResult;
  deleteVendor: (id: number) => { ok: true } | { ok: false; message: string };
  createSegment: (vendorId: number, input: SegmentInput) => SegmentResult;
  updateSegment: (id: number, input: SegmentInput) => SegmentResult;
  deleteSegment: (id: number) => { ok: true } | { ok: false; message: string };
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [vendors, setVendors] = useState<Vendor[]>(seedVendors);
  const [segments, setSegments] = useState<Segment[]>(seedSegments);
  const nextId = useRef(100);

  const takeId = () => {
    nextId.current += 1;
    return nextId.current;
  };

  const createVendor = useCallback(
    (name: string): VendorResult => {
      const message = validateVendorName(name, vendors);
      if (message) return { ok: false, message };
      const vendor: Vendor = { id: takeId(), name: name.trim() };
      setVendors((current) => [vendor, ...current]);
      return { ok: true, vendor };
    },
    [vendors],
  );

  const updateVendorName = useCallback(
    (id: number, name: string): VendorResult => {
      const message = validateVendorName(name, vendors, id);
      if (message) return { ok: false, message };
      const trimmed = name.trim();
      let updated: Vendor | undefined;
      setVendors((current) =>
        current.map((vendor) => {
          if (vendor.id !== id) return vendor;
          updated = { ...vendor, name: trimmed };
          return updated;
        }),
      );
      if (!updated) return { ok: false, message: "That vendor is no longer in the catalog." };
      return { ok: true, vendor: updated };
    },
    [vendors],
  );

  const deleteVendor = useCallback(
    (id: number) => {
      const message = deleteVendorBlockReason(id, segments);
      if (message) return { ok: false as const, message };
      setVendors((current) => current.filter((vendor) => vendor.id !== id));
      return { ok: true as const };
    },
    [segments],
  );

  const createSegment = useCallback(
    (vendorId: number, input: SegmentInput): SegmentResult => {
      const vendor = vendors.find((item) => item.id === vendorId);
      if (!vendor) {
        return { ok: false, field: "name", message: "That vendor is no longer in the catalog." };
      }
      const error = validateSegment(input, vendorId, segments);
      if (error) return { ok: false, ...error };
      const segment: Segment = {
        id: takeId(),
        name: input.name.trim(),
        description: input.description.trim(),
        segmentId: input.segmentId as number,
        akDspIpListId: null,
        storedActive: false,
        vendorId,
        targetingReferences: 0,
      };
      setSegments((current) => [segment, ...current]);
      return { ok: true, segment };
    },
    [segments, vendors],
  );

  const updateSegment = useCallback(
    (id: number, input: SegmentInput): SegmentResult => {
      const existing = segments.find((segment) => segment.id === id);
      if (!existing) {
        return { ok: false, field: "name", message: "That segment is no longer in the catalog." };
      }
      const vendor = vendors.find((item) => item.id === existing.vendorId);
      if (!vendor) {
        return { ok: false, field: "name", message: "That vendor is no longer in the catalog." };
      }
      const error = validateSegment(input, existing.vendorId, segments, existing);
      if (error) return { ok: false, ...error };
      const segment: Segment = {
        ...existing,
        name: input.name.trim(),
        description: input.description.trim(),
        segmentId: input.segmentId as number,
        storedActive: existing.akDspIpListId == null ? false : input.active,
      };
      setSegments((current) =>
        current.map((item) => (item.id === id ? segment : item)),
      );
      return { ok: true, segment };
    },
    [segments, vendors],
  );

  const deleteSegment = useCallback(
    (id: number) => {
      const existing = segments.find((segment) => segment.id === id);
      if (!existing) return { ok: false as const, message: "That segment is no longer in the catalog." };
      const message = deleteSegmentBlockReason(existing);
      if (message) return { ok: false as const, message };
      setSegments((current) => current.filter((segment) => segment.id !== id));
      return { ok: true as const };
    },
    [segments],
  );

  const value = useMemo<CatalogContextValue>(
    () => ({
      vendors,
      segments,
      createVendor,
      updateVendorName,
      deleteVendor,
      createSegment,
      updateSegment,
      deleteSegment,
    }),
    [
      vendors,
      segments,
      createVendor,
      updateVendorName,
      deleteVendor,
      createSegment,
      updateSegment,
      deleteSegment,
    ],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const value = useContext(CatalogContext);
  if (!value) {
    throw new Error("useCatalog must be used within CatalogProvider");
  }
  return value;
}
